import { formatCents } from '@src/common';
import { ProductPackage, SaleItem } from '@src/core/buyerApiClient';
import useTranslate from '@src/i18n/useTranslate';
import React, { useMemo } from 'react';

import { CustomAccordion } from '@planne-software/uni/Accordion';
import {
  PackageProductWrapper,
  Product,
  PackagePriceInfo,
  PackageOriginalPrice,
  PackageDiscountValue,
  PackageTotalPrice,
  PackagePrices,
  PackageTariffInfo,
  ProductName,
  PackageDates,
  PackageProductDetails,
  PackageProductDetailsContent,
} from '../styles';
import PackageItemDetail from './ProductPackageItem';
import { getFormattedTariffs, getPackageDates, calculatePackageTotal } from '@src/common/checkout';

interface PackageItemGroupProps {
  productPackage: ProductPackage;
  items: SaleItem[];
}

const ProductPackageGroup = (props: PackageItemGroupProps) => {
  const { productPackage, items } = props;

  const { i18n } = useTranslate();

  const packageDates = useMemo(() => getPackageDates(items), [items]);
  const formattedTariffs = useMemo(() => getFormattedTariffs(items), [items]);

  const fullPriceCentsPackage = useMemo(() => {
    return calculatePackageTotal(items, (item) => item.fullPriceCents);
  }, [items]);

  const totalPriceCentsPackage = useMemo(() => {
    return calculatePackageTotal(items, (item) => item.totalPriceCents);
  }, [items]);

  return (
    <Product key={`${productPackage.name}-${productPackage.id}`}>
      <ProductName>{productPackage.name}</ProductName>

      {packageDates && (
        <PackageDates>
          {i18n('cart_package_date')} {packageDates}
        </PackageDates>
      )}

      <PackageTariffInfo>
        {i18n('cart_package_tariff')} {formattedTariffs}
      </PackageTariffInfo>

      <PackagePrices>
        <PackagePriceInfo>
          {i18n('cart_package_previous_price')}
          <PackageOriginalPrice>{formatCents(fullPriceCentsPackage)}</PackageOriginalPrice>
        </PackagePriceInfo>

        <PackagePriceInfo>
          {i18n('cart_package_discount')}
          <PackageDiscountValue>-{formatCents(fullPriceCentsPackage - totalPriceCentsPackage)}</PackageDiscountValue>
        </PackagePriceInfo>

        <PackagePriceInfo>
          {i18n('cart_package_total')}
          <PackageTotalPrice>{formatCents(totalPriceCentsPackage)}</PackageTotalPrice>
        </PackagePriceInfo>
      </PackagePrices>

      <CustomAccordion
        title={<PackageProductDetails>{i18n('cart_package_product_details')}</PackageProductDetails>}
        showBorderBottom
        content={
          <PackageProductDetailsContent>
            <PackageProductWrapper>
              {items.map((item) => (
                <PackageItemDetail key={item.id} item={item} />
              ))}
            </PackageProductWrapper>
          </PackageProductDetailsContent>
        }
      />
    </Product>
  );
};

export default ProductPackageGroup;
